import { useModuleBEM } from "@b2bportal/core-themes";
import type {
  ActivityReservation,
  PaymentBreakdown,
} from "@b2bportal/rbc-activities-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import clsx from "clsx";
import styles from "./CheckoutBreakdownSection.module.scss";
import { useCheckoutBreakdown } from "./useCheckoutBreakdown";

interface CheckoutBreakdownSectionProps {
  reservation: ActivityReservation;
  paymentBreakdown?: PaymentBreakdown;
  className?: string;
}

export const CheckoutBreakdownSection = ({
  reservation,
  paymentBreakdown,
  className,
}: CheckoutBreakdownSectionProps) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "CheckoutBreakdownSection");
  const {
    lineItems,
    bookingFee,
    total,
    travelCreditsApplied,
    amountPaid,
    paymentMethod,
    redeemedPoints,
  } = useCheckoutBreakdown(reservation, paymentBreakdown);

  if (lineItems.length === 0) return null;

  return (
    <div className={clsx(block, className)}>
      <h3 className={cn("title")}>
        {t("core-activities.shopPage.priceBreakdown.title")}
      </h3>

      <div className={cn("divider")} />

      <div className={cn("line-items")}>
        {lineItems.map((lineItem, index) => (
          <div key={index} className={cn("line")}>
            <span>{lineItem.label}</span>
            <span>{lineItem.value}</span>
          </div>
        ))}
        <p className={cn("disclaimer")}>
          {t("core-activities.shopPage.priceBreakdown.disclaimer")}
        </p>
      </div>

      <div className={cn("divider")} />

      <div className={cn("line", ["total"])}>
        <span>{t("core-activities.shopPage.priceBreakdown.totalPrice")}</span>
        <span>{total}</span>
      </div>

      {travelCreditsApplied && (
        <>
          <div className={cn("divider")} />
          <div className={cn("line", ["redeemed"])}>
            <span>{travelCreditsApplied.label}</span>
            <span>{travelCreditsApplied.value}</span>
          </div>
        </>
      )}

      {redeemedPoints && (
        <>
          <div className={cn("divider")} />
          <div className={cn("line", ["redeemed"])}>
            <span>{redeemedPoints.label}</span>
            <span>{redeemedPoints.value}</span>
          </div>
        </>
      )}

      <div className={cn("divider")} />

      <div className={cn("line", ["total"])}>
        <span>{t("hotelTrips.amountPaid")}</span>
        <span>{amountPaid}</span>
      </div>
      {paymentMethod && (
        <div className={cn("line")}>
          <span>{t("tripsPaymentMethodTitle")}</span>
          <span>{paymentMethod}</span>
        </div>
      )}
    </div>
  );
};
